import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { yupResolver } from "@hookform/resolvers/yup";
import { useCreateChecklistItem, useDeleteChecklistItem, useUpdateChecklistItem, useChecklistItem } from "api/checklistItems";
import CustomReactSelect from "components/CustomReactSelect";
import HeaderCard from "components/DataTable/HeaderCard";
import ImageUploadField from "components/ImageUploadField";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import RideSelector from "components/RideSelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import useFilterStore from "store/useFilterStore";
import Swal from "sweetalert2";
import { getChecklistCategories, getFrequencyFilters, getStatusFilters, jsonToFormData, removeEmptyFields } from "utils/helper";
import { parentRoute } from ".";
import { formatDateTime } from "utils/helper";

const categories = getChecklistCategories(false);
const types = getFrequencyFilters(false);

const checklistSchema = yup.object({
    ci_name: yup.string().trim().required("Name is required"),
    ci_bm_name: yup.string().trim().required("BM Name is required"),
    ci_category: yup.string().required("Category is required"),
    ci_type: yup.string().required("Type is required"),
    ci_ride_id: yup.string().required("Ride is required"),
    ci_check_duration: yup
        .number()
        .transform((value, originalValue) =>
            String(originalValue).trim() === "" ? null : value
        )
        .typeError("Check duration must be a number")
        .min(0, "Check duration cannot be negative")
        .nullable(),
});

const AddChecklistItems = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const { getFilters } = useFilterStore();
    const [formData, setFormData] = useState({
        ci_ride_id: "",
        ci_description: "",
        ci_img_filename: null,
        ci_bm_name: "",
        ci_name: "",
        ci_check_duration: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const statusSelection = getStatusFilters(false)
    const id = Number(location.pathname.split('/')[2]);

    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add-checklist`),
        [location.pathname]
    );

    const checklistItemQuery = useChecklistItem(id, { enabled: !isAdd });
    const isDetailsLoading = !isAdd && checklistItemQuery.isLoading;

    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
        reset: resetForm
    } = useForm({
        resolver: yupResolver(checklistSchema),
        mode: "onSubmit",
        defaultValues: {
            ci_name: "",
            ci_bm_name: "",
            ci_category: "",
            ci_type: "",
            ci_ride_id: "",
            ci_check_duration: "",
            ci_img_filename: null,
            ci_status: "",
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    const [formErrors, setFormErrors] = useState({});
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    const createChecklistItemMutation = useCreateChecklistItem();
    const updateChecklistItemMutation = useUpdateChecklistItem();
    const deleteChecklistItemMutation = useDeleteChecklistItem();

    useEffect(() => {
        if (isAdd) return;

        if (!id || isNaN(id)) {
            navigate(parentRoute, { state: location.state });
            return;
        }

        if (checklistItemQuery.isError) {
            toast.error("Failed to find checklist item data");
            navigate(parentRoute, { state: location.state });
            return;
        }

        const details = checklistItemQuery.data;
        if (details) {
            setFormData({
                id: details.id,
                ci_name: details.name,
                ci_bm_name: details.bm_name,
                ci_img_filename: details.img_filename,
                ci_ride_id: details.ride_id?.id || "",
                ci_category: details.category?.id || "",
                ci_type: details.type?.id || "",
                ci_status: details.status?.id || "",
                ci_check_duration: details.check_duration || ""
            });
        }
    }, [checklistItemQuery.data, checklistItemQuery.isError, id, isAdd, location.state, navigate]);

    useEffect(() => {
        if (!isAdd) return;

        const preselectedRideId = Number(location.state?.ride_id);
        if (!preselectedRideId || isNaN(preselectedRideId)) return;

        setFormData(prev => {
            if (prev.ci_ride_id) return prev;
            return {
                ...prev,
                ci_ride_id: preselectedRideId,
            };
        });
    }, [isAdd, location.state?.ride_id]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);


    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            await deleteChecklistItemMutation.mutateAsync(formData.id);
            toast.success("Checklist items deleted successfully");
            navigate(parentRoute);
        }
    }

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            let passData = jsonToFormData(removeEmptyFields(formData));

            if (isAdd) {
                await createChecklistItemMutation.mutateAsync(passData);
                toast.success("Checklist items added successfully");

                const selectedRideId = Number(formData?.ci_ride_id || location.state?.ride_id || "");
                const continueResult = await Swal.fire({
                    title: "Create another checklist item?",
                    text: "Do you want to continue creating checklist items for this ride?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes, continue",
                    cancelButtonText: "No, back to list",
                });

                if (continueResult.isConfirmed) {
                    resetForm()
                    setFormData({
                        ci_ride_id: !isNaN(selectedRideId) ? selectedRideId : "",
                        ci_description: "",
                        ci_img_filename: null,
                        ci_bm_name: "",
                        ci_name: "",
                        ci_check_duration: "",
                        ci_category: "",
                        ci_type: "",
                        ci_status: "",
                    });
                    setFormErrors({});
                } else {
                    navigate(parentRoute, { state: location.state });
                }
            } else {
                await updateChecklistItemMutation.mutateAsync({ id: formData.id, itemData: passData });
                toast.success("Checklist items updated successfully");
            }
        } catch (err) {
            toast.error(err?.data?.message || (isAdd ? "Failed to create checklist items" : "Failed to update checklist items"));
        } finally {
            setIsLoading(false);
        }
    }, [formData, navigate, isAdd, createChecklistItemMutation, updateChecklistItemMutation, location.state, resetForm]);

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger();
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            await handleSubmit(e);
        },
        [trigger, handleSubmit]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Create a New Checklist Item" : "Edit Checklist Item"}
                                showBackButton={true}
                                backTo={parentRoute}
                                getBackState={() => {
                                    const record = getFilters(parentRoute);
                                    return record?.baseTable ? { ...record.baseTable } : undefined;
                                }}
                                onBackDone={() => setPrevSelectedRow(id, parentRoute)}
                            />
                            {isDetailsLoading ? <Loading /> :
                                <MDBox pt={3} px={2} pb={2}>
                                    {!isAdd && (
                                        <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                            <MDTypography variant="body2" color="text" gutterBottom>
                                                Created At: {checklistItemQuery.data?.created_at ? formatDateTime(checklistItemQuery.data.created_at) : '-'}
                                            </MDTypography>
                                            <MDTypography variant="body2" color="text" gutterBottom>
                                                Last Updated: {checklistItemQuery.data?.updated_at ? formatDateTime(checklistItemQuery.data.updated_at) : '-'}
                                            </MDTypography>
                                        </MDBox>
                                    )}
                                    <form noValidate onSubmit={handleValidatedSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3} style={{ flexGrow: 1 }}>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Name"
                                                        name="ci_name"
                                                        multiline
                                                        minRows={2}
                                                        value={formData?.ci_name || ''}
                                                        onChange={handleChange}
                                                        required
                                                        error={!!formErrors.ci_name}
                                                        helperText={formErrors.ci_name}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="BM Name"
                                                        name="ci_bm_name"
                                                        multiline
                                                        minRows={2}
                                                        value={formData?.ci_bm_name || ''}
                                                        onChange={handleChange}
                                                        required
                                                        error={!!formErrors.ci_bm_name}
                                                        helperText={formErrors.ci_bm_name}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Category"
                                                        name="ci_category"
                                                        variant="outlined"
                                                        value={categories.find(item => item.value === (formData?.ci_category)) || null}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'ci_category', value: selectedOption?.value } })}
                                                        options={categories}
                                                        required
                                                        error={!!formErrors.ci_category}
                                                        helperText={formErrors.ci_category}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Type"
                                                        name="ci_type"
                                                        variant="outlined"
                                                        value={types.find(item => item.value === (formData?.ci_type)) || null}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'ci_type', value: selectedOption?.value } })}
                                                        options={types}
                                                        required
                                                        error={!!formErrors.ci_type}
                                                        helperText={formErrors.ci_type}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <RideSelector
                                                        label="Ride"
                                                        value={formData?.ci_ride_id}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'ci_ride_id', value: selectedOption } })}
                                                        required
                                                        error={!!formErrors.ci_ride_id}
                                                        helperText={formErrors.ci_ride_id}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Check duration (minutes)"
                                                        name="ci_check_duration"
                                                        type="number"
                                                        inputProps={{ min: 0 }}
                                                        value={formData?.ci_check_duration}
                                                        onChange={handleChange}
                                                        error={!!formErrors.ci_check_duration}
                                                        helperText={formErrors.ci_check_duration}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <ImageUploadField
                                                        value={formData?.ci_img_filename}
                                                        onChange={(file) => setFormData(prev => ({ ...prev, ci_img_filename: file }))}
                                                        name="ci_img_filename"
                                                        label="Checklist Item Image *"
                                                        error={!!formErrors.ci_img_filename}
                                                        helperText={formErrors.ci_img_filename}
                                                    />
                                                </Grid>

                                                {!isAdd && <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Status"
                                                        name="ci_status"
                                                        variant="outlined"
                                                        value={statusSelection.find(status => status.value === (formData?.ci_status ?? formData?.ci_status?.id)) || null}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'ci_status', value: selectedOption?.value } })}
                                                        options={statusSelection}
                                                        required
                                                        error={!!formErrors.ci_status}
                                                        helperText={formErrors.ci_status}
                                                    />
                                                </Grid>}

                                                <Grid item xs={12}>
                                                    <Grid container>
                                                        <Grid item>
                                                            {!isAdd && <MDButton
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleDelete}
                                                                loading={isLoading}
                                                            >
                                                                Delete
                                                            </MDButton>}
                                                        </Grid>
                                                        <Grid item xs />
                                                        <Grid item>
                                                            <MDBox display="flex" justifyContent="flex-end">
                                                                <MDButton
                                                                    variant="contained"
                                                                    color="primary"
                                                                    type="submit"
                                                                    disabled={!formData?.ci_img_filename}
                                                                    loading={isLoading}
                                                                >
                                                                    Submit
                                                                </MDButton>
                                                            </MDBox>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>}
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default AddChecklistItems;
